import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Check, X, ShieldAlert, ShieldCheck } from 'lucide-react'
import type { ObjectVersion } from '@/types/entities'
import { useRetrieveVersion, useObject, useConversionOptions } from '../hooks'
import { ConversionFormatPicker, buildConvertFormats, type ConversionMode } from './ConversionFormatPicker'
import { useToast } from '@/hooks/use-toast'
import { formatBytes } from '../utils/formatSize'
import { buildIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { AnalyticsReportDialog } from './AnalyticsReportDialog'
import { ScanFirstOption } from './ScanFirstOption'
import { formatDateTime } from '@/lib/dateUtils'

interface RetrieveVersionDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    version: ObjectVersion | null
    objectID: string | null
}

export function RetrieveVersionDialog({
    open,
    onOpenChange,
    version,
    objectID,
}: RetrieveVersionDialogProps) {
    const { t, i18n } = useTranslation()
    const { toast } = useToast()
    const retrieveMutation = useRetrieveVersion()
    // Scan this version before it is copied to egress (offered only while the setting is on).
    const [scanFirst, setScanFirst] = useState(false)
    const [confirming, setConfirming] = useState(false)
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)
    const [retrievedVersion, setRetrievedVersion] = useState<string>('')
    const [showAnalytics, setShowAnalytics] = useState(false)
    // Object-level icon + description (the version row doesn't carry them).
    const { data: object } = useObject(open ? objectID : null)
    // Egress conversion. Default 'none' — raw is what the vault holds and what our restore agent
    // uses, so converting is opted into, never assumed.
    const [conversionMode, setConversionMode] = useState<ConversionMode>('none')
    const [conversionAllTargets, setConversionAllTargets] = useState<string[]>([])
    const [conversionPerObject, setConversionPerObject] = useState<Record<string, string[]>>({})
    const { data: conversionData, isLoading: isLoadingConversion } = useConversionOptions(
        objectID,
        { mode: 'version' },
        open,
    )

    if (!version || !objectID) {
        return null
    }

    const isRedFlagged = version.objectVersionStatus === 2
    const showWarning = isRedFlagged && !confirming

    // A scan report exists when analyticsText holds parseable JSON with a status.
    const hasAnalytics =
        !!version.analyticsText &&
        version.analyticsText.trim() !== '' &&
        (() => {
            try {
                return !!JSON.parse(version.analyticsText).status
            } catch {
                return false
            }
        })()
    const status = version.objectVersionStatus
    const StatusShield = status === 4 ? ShieldCheck : ShieldAlert
    const shieldColor =
        status === 4 ? 'text-green-500' : status === 3 ? 'text-amber-500' : 'text-red-500'

    const handleConfirm = async () => {
        if (showWarning) {
            setConfirming(true)
            return
        }

        // Call the retrieve mutation
        try {
            const result = await retrieveMutation.mutateAsync({
                objectID,
                versionID: version.versionID,
                recoverRedObjects: isRedFlagged ? 'Yes' : 'No',
                scanFirst,
                convertFormats: buildConvertFormats(
                    conversionMode,
                    conversionData?.objects ?? [],
                    conversionAllTargets,
                    conversionPerObject,
                ),
            })

            if (result.success) {
                const versionText = `${version.objectPrimary} ${version.objectSecondary}`
                setRetrievedVersion(versionText)
                setShowSuccessDialog(true)
                setConfirming(false)
            } else {
                toast({
                    title: t('objects.versions.retrieveError'),
                    description: t('objects.versions.retrieveErrorMessage'),
                    variant: 'destructive',
                })
            }
        } catch (error) {
            toast({
                title: t('objects.versions.retrieveError'),
                description: t('objects.versions.retrieveErrorMessage'),
                variant: 'destructive',
            })
        } finally {
            setConfirming(false)
        }
    }

    const handleCancel = () => {
        setConfirming(false)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[900px]">
                <DialogHeader>
                    <DialogTitle>{t('objects.versions.retrieveVersion')}</DialogTitle>
                    <DialogDescription>
                        {t('objects.versions.retrieveVersionDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Summary header: object identity on the left, scan status on the right */}
                    <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                            <div className="flex items-center gap-3">
                                <Label className="text-xs font-medium text-primary w-28 shrink-0">
                                    {t('objects.objectID')}
                                </Label>
                                <div className="flex items-center gap-2">
                                    <p className="text-base font-bold">{objectID || '-'}</p>
                                    {object?.iconFilename && (
                                        <EntityIcon
                                            src={buildIconPath(object.iconFilename)}
                                            alt=""
                                            className="h-8 w-8"
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0">
                                    {t('objects.objectDescription')}
                                </Label>
                                <p className="text-sm text-foreground">{object?.objectDescription || '-'}</p>
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                            <div className="flex items-center gap-2">
                                {status != null ? (
                                    <AnalyticsStatusIndicator status={status} size="h-4 w-4" />
                                ) : (
                                    <span className="text-sm text-muted-foreground">-</span>
                                )}
                                {hasAnalytics && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 gap-1.5 px-2"
                                        onClick={() => setShowAnalytics(true)}
                                    >
                                        <StatusShield className={`h-3.5 w-3.5 ${shieldColor}`} />
                                        {t('objects.versions.viewAnalytics', 'View report')}
                                    </Button>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {version.vaultDatetime
                                    ? formatDateTime(version.vaultDatetime, i18n.language)
                                    : '-'}
                            </p>
                        </div>
                    </div>

                    {/* Version Details */}
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectName')}:
                                </span>
                                <span className="ml-2 break-words">{version.objectName || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectPrimary')}:
                                </span>
                                <span className="ml-2">{version.objectPrimary || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectSecondary')}:
                                </span>
                                <span className="ml-2">{version.objectSecondary || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectSize')}:
                                </span>
                                <span className="ml-2">
                                    {version.objectSize !== null && version.objectSize !== undefined
                                        ? formatBytes(version.objectSize)
                                        : '-'}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.vaultDatetime')}:
                                </span>
                                <span className="ml-2">
                                    {version.vaultDatetime
                                        ? formatDateTime(version.vaultDatetime, i18n.language)
                                        : '-'}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.vaultLocation')}:
                                </span>
                                <span className="ml-2">{version.vaultLocation || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.objectLocation')}:
                                </span>
                                <span className="ml-2">{version.objectLocation || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.vaultName')}:
                                </span>
                                <span className="ml-2">{version.vaultName || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.vaultID')}:
                                </span>
                                <span className="ml-2">{version.vaultID || '-'}</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.anomaly')}:
                                </span>
                                <span className="ml-2 flex items-center gap-1">
                                    {version.anomaly && version.anomaly.toLowerCase() === 'yes' ? (
                                        <>
                                            <X className="h-3 w-3 text-red-500" />
                                            <span>Yes</span>
                                        </>
                                    ) : (
                                        <>
                                            <Check className="h-3 w-3 text-green-500" />
                                            <span>No</span>
                                        </>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.versionEncryption')}:
                                </span>
                                <span className="ml-2 flex items-center gap-1">
                                    {version.versionEncryption === 1 || version.versionEncryption === '1' ? (
                                        <>
                                            <Check className="h-3 w-3 text-green-500" />
                                            <span>Yes</span>
                                        </>
                                    ) : (
                                        <>
                                            <X className="h-3 w-3 text-muted-foreground" />
                                            <span>No</span>
                                        </>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-muted-foreground min-w-[140px]">
                                    {t('objects.versions.analytics')}:
                                </span>
                                <span className="ml-2 flex items-center gap-2">
                                    {status != null ? (
                                        <AnalyticsStatusIndicator status={status} size="h-4 w-4" />
                                    ) : (
                                        <span>-</span>
                                    )}
                                    {hasAnalytics && (
                                        <button
                                            type="button"
                                            onClick={() => setShowAnalytics(true)}
                                            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                                        >
                                            <StatusShield className={`h-3.5 w-3.5 ${shieldColor}`} />
                                            {t('objects.versions.viewAnalytics', 'View report')}
                                        </button>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Convert disk images on egress. Offered only when this object actually
                        carries a readable disk image — see ConversionFormatPicker. */}
                    <ConversionFormatPicker
                        options={conversionData?.objects ?? []}
                        isLoading={isLoadingConversion}
                        mode={conversionMode}
                        onModeChange={setConversionMode}
                        allTargets={conversionAllTargets}
                        onAllTargetsChange={setConversionAllTargets}
                        perObject={conversionPerObject}
                        onPerObjectChange={setConversionPerObject}
                    />

                    {/* Warning for Red Flagged Versions */}
                    {showWarning && (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                {t('objects.versions.redFlaggedWarning')}
                            </AlertDescription>
                        </Alert>
                    )}
                    <ScanFirstOption checked={scanFirst} onChange={setScanFirst} versionIds={[version.versionID]} disabled={retrieveMutation.isPending} />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel} disabled={retrieveMutation.isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={retrieveMutation.isPending}
                        variant={isRedFlagged ? 'destructive' : 'default'}
                    >
                        {retrieveMutation.isPending
                            ? t('common.processing')
                            : showWarning
                              ? t('common.yes')
                              : t('objects.versions.confirmRetrieve')}
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Analytics scan report */}
            <AnalyticsReportDialog
                open={showAnalytics}
                onOpenChange={setShowAnalytics}
                analyticsText={version.analyticsText || null}
                version={version}
                objectId={objectID}
                objectDescription={object?.objectDescription}
                iconFilename={object?.iconFilename}
            />

            {/* Success Dialog */}
            <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.versions.retrieveSuccess')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.versions.retrieveSuccessMessage', {
                                version: retrievedVersion,
                            })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground">
                            {t('objects.versions.retrieveNeedsApproval')}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                setShowSuccessDialog(false)
                                onOpenChange(false)
                            }}
                        >
                            {t('common.yes')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    )
}
